import { useState, useRef, useEffect } from "react";
import { useApi } from "../../hooks/use-api";
import {
  apiStudentStudys,
  API_SUBJECTS_UPSC,
  API_MCQ_TNPSC,
  apiChatHistoryFilterTnpsc,
  API_MEDIUM_TNPSC,
  API_PAPERS_UPSC,
  API_UNITS_TNPSC,
  API_LESSONS_TNPSC,
  API_AVATAR_START,
  API_AVATAR_STOP,
  API_AVATAR_LOG,
  API_AVATAR_UPSC_ASK,
  API_UPSC_ASSET_URL,
  API_AUDIO_CHAT,
  API_VIDEO_CHAT,
  API_VIDEO_MARK,
  API_LEARNING_CHALLENGE_UPDATE,
} from "../../utils/api-routes";
import { useSingleton } from "../../hooks/use-singleton";
import { useNotify } from "../../hooks/use-notify";

const useHandler = () => {
  const { get, post } = useApi();
  const { toggleNotify } = useNotify();
  const { mail, user, role, medium, board, examType } = useSingleton();
  const [inputText, setInputText] = useState("");
  const [conversation, setConversation] = useState([]);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isEnable, setIsEnable] = useState(false);
  const [mode, setMode] = useState(false);
  const [avatarSession, setAvatarSession] = useState(null);
  const [lastAvatarSessionId, setLastAvatarSessionId] = useState(null);
  const [avatarToast, setAvatarToast] = useState(null);
  const [isLiveAvatarLoading, setIsLiveAvatarLoading] = useState(false);
  const [isHistoryEmpty, setIsHistoryEmpty] = useState(false);
  const [showAskBox, setShowAskBox] = useState(true);
  const [audioRes, setAudioRes] = useState({});
  const [videoRes, setVideoRes] = useState({});
  const [selectedMedium, setSelectedMedium] = useState("");
  const [selectedPart, setSelectedPart] = useState("");
  const [selectedUnit, setSelectedUnit] = useState("");
  const [selectedLesson, setSelectedLesson] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState("");
  const [selectedChapter, setSelectedChapter] = useState("");
  const [selectedLanguage, setSelectedLanguage] = useState("");
  const [assetData, setAssetData] = useState([]);
  const [popupMedia, setPopupMedia] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [question, setQuestion] = useState("");

  const mediumRef = useRef(null);
  const partRef = useRef(null);
  const unitRef = useRef(null);
  const lessonRef = useRef(null);
  const subjectRef = useRef(null);
  const chapterRef = useRef(null);
  const languageRef = useRef(null);
  const inputRef = useRef(null);
  const audioRef = useRef(null);
  const videoRef = useRef(null);

  const [classdetails, setClassDetails] = useState({});
  const [mediums, setMediums] = useState([]);
  const [parts, setParts] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [units, setUnits] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [chapters, setChapters] = useState([]);

  const isStartEnabled =
    !!selectedSubject &&
    !!selectedLanguage &&
    selectedLesson.length > 0 &&
    !!selectedPart &&
    !!selectedUnit;

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSend = async (text) => {
    const question = (typeof text === "string" ? text : inputText).trim();
    if (!question) return;

    // Add user message
    const userMessage = {
      sender: "user",
      text: question,
      time: new Date().toISOString(),
      isNew: true,
    };
    setConversation((prev) => [...prev, userMessage]);

    // Clear input box
    setInputText("");

    setIsLoading(true);

    // 2️⃣ Add temporary loading bot message
    setConversation((prev) => [
      ...prev,
      {
        sender: "bot",
        text: "Loading...",
        isLoading: true,
        time: new Date().toISOString(),
      },
    ]);

    try {
      const payload = {
        mail: mail,
        board: board,
        clas: classdetails?.data?.class,
        medium: medium,
        paper: selectedPart,
        subject: selectedSubject,
        unit: selectedUnit,
        lesson: selectedLesson.includes("All Lessons")
          ? "All Lessons"
          : selectedLesson.join(", "),
        question: question, // Fixed state desync bug
        language: selectedLanguage,
      };

      if (mode) {
        // --- LIVE AVATAR INTERACTION ---
        const response = await post(API_AVATAR_UPSC_ASK, payload, {
          headers: {
            "x-user-mail": mail || user?.data?.email || "",
          },
        });

        if (response.status !== 200 && response.status !== 201) {
          throw new Error(`Avatar Ask TNPSC failed: ${response.status}`);
        }

        // Drop the loading bubble, Avatar will respond audibly
        setConversation((prev) => prev.filter((msg) => !msg.isLoading));
      } else {
        // --- STANDARD TEXT CHATBOT ---
        const response = await post(API_MCQ_TNPSC, payload);

        setConversation((prev) =>
          prev.map((msg) =>
            msg.isLoading
              ? {
                  sender: "bot",
                  text:
                    response?.data?.answer ||
                    "Sorry, I couldn't understand that.",
                  audioPath: response?.data?.audio_path || null,
                  time: new Date().toISOString(),
                  isNew: true,
                }
              : msg,
          ),
        );
      }
    } catch (error) {
      console.error("API Error:", error);
      setConversation((prev) =>
        prev.map((msg) =>
          msg.isLoading
            ? {
                sender: "bot",
                text: "Sorry, there was an error processing your request.",
                time: new Date().toISOString(),
              }
            : msg,
        ),
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        partRef.current?.contains(e.target) ||
        unitRef.current?.contains(e.target) ||
        lessonRef.current.contains(e.target) ||
        mediumRef.current?.contains(e.target) ||
        subjectRef.current?.contains(e.target) ||
        chapterRef.current?.contains(e.target) ||
        languageRef.current?.contains(e.target)
      )
        return;

      setOpenDropdown(null);
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getUserStudiesDetails = async () => {
    try {
      const res = await get(apiStudentStudys(mail));
      if (res) {
        // console.log(res?.data)
        setClassDetails(res?.data);
        const payload = {
          board: board,
          clas: res?.data?.data?.class,
        };
        getMediumDetails(payload);
      } else {
        alert(res?.message);
      }
    } catch (e) {
      console.log("Error on getting user studiesdetails", e);
    }
  };

  const getMediumDetails = async (payload) => {
    setIsLoading(true);
    try {
      const res = await post(API_MEDIUM_TNPSC, payload);
      if (res) {
        setMediums(res?.data?.mediums);
        setIsLoading(false);
      } else {
        alert(res?.message);
      }
    } catch (e) {
      setIsLoading(false);
      console.log("Error on getting Subject Details", e);
    }
  };

  const getPartDetails = async (payload) => {
    setIsLoading(true);
    try {
      const res = await post(API_PAPERS_UPSC, payload);
      if (res) {
        setParts(res?.data?.papers);
        setIsLoading(false);
      } else {
        alert(res?.message);
      }
    } catch (e) {
      setIsLoading(false);
      console.log("Error on getting Subject Details", e);
    }
  };

  const getUnitsDetails = async (payload) => {
    setIsLoading(true);
    try {
      const res = await post(API_UNITS_TNPSC, payload);
      if (res) {
        setUnits(res?.data?.units);
        setIsLoading(false);
      } else {
        alert(res?.message);
      }
    } catch (e) {
      setIsLoading(false);
      console.log("Error on getting Subject Details", e);
    }
  };

  console.log(assetData, "dfghjkl;");

  const getLessonsDetails = async (payload) => {
    setIsLoading(true);
    try {
      const res = await post(API_LESSONS_TNPSC, payload);
      if (res) {
        setLessons(res?.data?.lessons);
        setIsLoading(false);
      } else {
        alert(res?.message);
      }
    } catch (e) {
      setIsLoading(false);
      console.log("Error on getting Subject Details", e);
    }
  };

  const getSubjectDetails = async (payload) => {
    setIsLoading(true);
    try {
      const res = await post(API_SUBJECTS_UPSC, payload);
      if (res) {
        setSubjects(res?.data?.subjects);
        setIsLoading(false);
      } else {
        alert(res?.message);
      }
    } catch (e) {
      setIsLoading(false);
      console.log("Error on getting Subject Details", e);
    }
  };

  useEffect(() => {
    getUserStudiesDetails();
  }, []);

  useEffect(() => {
    if (!medium || !classdetails?.data?.class) return;
    const payload = {
      board: board,
      clas: classdetails?.data?.class,
      medium: medium,
    };

    getPartDetails(payload);
  }, [medium, classdetails?.data?.class]);

  useEffect(() => {
    if (!selectedPart || !classdetails?.data?.class) return;
    setSelectedSubject("");
    setSelectedUnit("");
    setSelectedLesson([]);

    setSubjects([]);
    setUnits([]);
    setLessons([]);

    const payload = {
      board: board,
      clas: classdetails?.data?.class,
      medium: medium,
      paper: selectedPart,
    };

    getSubjectDetails(payload);
  }, [selectedPart]);

  useEffect(() => {
    if (!selectedSubject || !classdetails?.data?.class) return;

    setSelectedUnit("");
    setSelectedLesson([]);

    setUnits([]);
    setLessons([]);

    const payload = {
      board: board,
      clas: classdetails?.data?.class,
      medium: medium,
      paper: selectedPart,
      subject: selectedSubject,
    };

    getUnitsDetails(payload);
  }, [selectedSubject]);

  useEffect(() => {
    if (!selectedUnit || !classdetails?.data?.class) return;

    setSelectedLesson([]);
    setLessons([]);

    const payload = {
      board: board,
      clas: classdetails?.data?.class,
      medium: medium,
      paper: selectedPart,
      subject: selectedSubject,
      unit: selectedUnit,
    };
    getLessonsDetails(payload);
  }, [selectedUnit]);

  // useEffect(()=>{
  //     setConversation([]);
  //     setIsEnable(false);
  // },[selectedSubject, selectedChapter, selectedLanguage])

  useEffect(() => {
    if (!isEnable) {
      setConversation([]);
    }
  }, [selectedSubject, selectedChapter, selectedLesson, selectedLanguage]);

  const onClickStart = async () => {
    setConversation([]);
    setIsEnable(true);
    setIsHistoryEmpty(false);
  };

  const onClickLearnVideo = async () => {
    setConversation([]);
    setIsHistoryEmpty(false);
    setShowPopup(true);

    try {
      const payload = {
        mail: mail,
        board: classdetails?.data?.board,
        clas: classdetails?.data?.class,
        medium: medium,
        paper: selectedPart,
        subject: selectedSubject,
        unit: selectedUnit,
        lesson: selectedLesson.includes("All Lessons")
          ? "All Lessons"
          : selectedLesson.join(", "),
        language: selectedLanguage,
      };

      const res = await post(API_UPSC_ASSET_URL, payload);

      if (res) {
        setAssetData(res?.data?.assets || []);
      }
    } catch (error) {
      console.error("API error:", error);
    }
  };

  const getHistoryDetails = async () => {
    try {
      const res = await get(
        apiChatHistoryFilterTnpsc(
          mail,
          "CBSE",
          classdetails?.data?.class,
          medium,
          selectedPart,
          selectedUnit,
          selectedChapter,
          selectedLesson.includes("All Lessons")
            ? "All Lessons"
            : selectedLesson.join(", "),
          selectedLanguage,
        ),
      );
      if (res?.data?.history.length) {
        const formattedConversation = [];

        res?.data?.history?.forEach((item) => {
          // const payload = item?.payload;

          // user message
          formattedConversation.push({
            sender: "user",
            text: item.user_question,
            time: item.created_at,
          });

          // bot message
          formattedConversation.push({
            sender: "bot",
            text: item?.ai_answer,
            audioPath: item?.audio_path || null,
          });
        });

        setConversation(formattedConversation);
        setIsHistoryEmpty(false);
      } else {
        setIsHistoryEmpty(true);
      }
    } catch (e) {
      console.log("ERROR IS COMES FROM GETHISTORY API", e);
    }
  };

  const onClickHistroy = async () => {
    if (isStartEnabled) {
      await getHistoryDetails();
      setIsEnable(true);
    }
  };

  const onToggleBtn = () => {
    if (role !== "guest") {
      setMode((p) => !p);
      setConversation([]);
      setIsEnable(false);
      setAvatarSession(null);
    } else {
      toggleNotify({
        type: "success",
        title: "Message",
        description: "Please Subscribe",
      });
    }
  };

  const onLiveAvatarApi = async () => {
    setIsEnable(true);
    setIsLiveAvatarLoading(true);

    setConversation([
      { sender: "bot", text: "Connecting to Live Avatar...", isLoading: true },
    ]);

    try {
      const response = await post(
        `${API_AVATAR_START}?language=${selectedLanguage}`,
        {},
        {
          headers: {
            "x-user-mail": mail || user?.data?.email || "",
          },
        },
      );

      console.log("AVATAR API STATUS:", response.status);
      const data = response.data;
      console.log("AVATAR START RESPONSE:", data);

      if (response.status === 200 || response.status === 201) {
        setAvatarSession(data || { connected: true });
        if (data?.session_id) setLastAvatarSessionId(data.session_id);
        setIsLiveAvatarLoading(false);
        setConversation([
          {
            sender: "bot",
            text: "Live Avatar connected successfully! You can now start interacting.",
          },
        ]);
      } else {
        throw new Error(data?.message || `Error: ${response.status}`);
      }
    } catch (err) {
      console.error("Error starting Avatar API:", err);
      setIsEnable(false);
      const msg = err?.message || "Unable to connect to Live Avatar.";
      setAvatarToast({
        type: "error",
        message:
          msg.includes("502") || msg.includes("403")
            ? "Unable to connect: Avatar service unavailable."
            : msg,
      });
      setTimeout(() => setAvatarToast(null), 4000);
      setConversation([]);
    } finally {
      setIsLiveAvatarLoading(false);
    }
  };

  const onEndAvatarInteraction = async () => {
    setConversation([]);
    setIsEnable(false);
    setAvatarSession(null);
    try {
      const response = await post(
        API_AVATAR_STOP,
        {},
        {
          headers: {
            "x-user-mail": mail || user?.data?.email || "",
          },
        },
      );
      console.log("AVATAR STOP RESPONSE STATUS:", response.status);
    } catch (err) {
      console.error("Error stopping Avatar API:", err);
    }
  };

  const downloadAvatarLog = async () => {
    const sid = avatarSession?.session_id || lastAvatarSessionId;
    if (!sid) {
      alert("No active avatar session found to download logs.");
      return;
    }
    try {
      const response = await get(`${API_AVATAR_LOG}?session_id=${sid}`, {
        headers: {
          "x-user-mail": mail || user?.data?.email || "",
        },
      });
      if (response && response.status === 200) {
        if (response.data?.pdf_url) {
          window.open(response.data.pdf_url, "_blank");
        } else {
          alert("Unable to generate log PDF.");
        }
      }
    } catch (err) {
      console.error("Error downloading log:", err);
      alert("Error downloading log file.");
    }
  };

  const aiAudioChat = async (filters, audio_content) => {
    const payload = {
      Audio_content: audio_content,
      // filters: audio_content,
      Question: question,
      language: "English",
    };

    try {
      const res = await post(API_AUDIO_CHAT, payload);
      if (res) {
        setAudioRes(res?.data);
      }
    } catch (e) {
      console.log("ERROR COMES FROM AUDIO CHAT API", e);
    }
  };

  const aiVideoChat = async (video_content) => {
    const payload = {
      // Video_content: video_content,
      // filters: video_content,
      filters: {
        board: board,
        clas: classdetails?.data?.class, // 👈 UPSC
        medium: medium,
        paper: selectedPart,
        subject: selectedSubject,
        unit: selectedUnit,
        lesson: selectedLesson.includes("All Lessons")
          ? "All Lessons"
          : selectedLesson.join(", "),
      },
      Question: question,
      language: "English",
    };

    try {
      const res = await post(API_VIDEO_CHAT, payload);
      if (res) {
        setVideoRes(res?.data);
      }
    } catch (e) {
      console.log("ERROR COMES FROM AI VIDEO CHAT API", e);
    }
  };

  const handleClearChat = () => {
    setConversation([]); // clear messages only
  };

  const handleNewChat = () => {
    // clear chat
    setConversation([]);

    // reset filters
    setSelectedPart(null);
    setSelectedSubject(null);
    setSelectedUnit(null);
    setSelectedLesson([]);
    setSelectedChapter(null);
    setSelectedLanguage(null);
    setSelectedMedium(null);

    // reset input
    setInputText("");
    setOpenDropdown(null);

    // ✅ IMPORTANT
    setIsEnable(false); // 👈 this will hide input section

    // optional resets
    setIsHistoryEmpty(false);
    onEndAvatarInteraction?.();
  };

  const learningChallengeProgress = async () => {
    const payload = {
      email: mail,
      action_type: "video",
      watch_percentage: 100,
      questions_attempted: 0,
      study_minutes: 0,
    };
    try {
      const res = await post(API_LEARNING_CHALLENGE_UPDATE, payload);
      if (res) {
        console.log(res);
      }
    } catch (e) {
      console.log("ERROR COMES FROM LEARNING CHALLANGE API", e);
    }
  };

  const videoMarkCompleted = async (videoUrl) => {
    const payload = {
      user_email: mail,
      video_url: videoUrl,
      course_type: examType,
      filters: {
        clas: classdetails?.data?.class, // 👈 UPSC
        medium: medium,
        paper: selectedPart,
        subject: selectedSubject,
        unit: selectedUnit,
        lesson: selectedLesson.includes("All Lessons")
          ? "All Lessons"
          : selectedLesson.join(", "),
      },
    };
    try {
      const res = await post(API_VIDEO_MARK, payload);
      if (res.data) {
        await learningChallengeProgress();
      }
    } catch (e) {
      console.log("ERROR COMES FROM VIDEO MARK COMPELETED", e);
    }
  };

  return {
    handleClearChat,
    handleNewChat,
    subjectRef,
    chapterRef,
    languageRef,
    conversation,
    selectedChapter,
    selectedLanguage,
    selectedSubject,
    openDropdown,
    inputText,
    classdetails,
    subjects,
    chapters,
    isLoading,
    user,
    isStartEnabled,
    isEnable,
    mode,
    avatarSession,
    avatarToast,
    selectedMedium,
    selectedLesson,
    selectedPart,
    selectedUnit,
    mediums,
    parts,
    lessons,
    units,
    mediumRef,
    partRef,
    lessonRef,
    unitRef,
    isHistoryEmpty,
    role,
    onToggleBtn,
    onClickHistroy,
    onClickStart,
    setSelectedSubject,
    setSelectedChapter,
    setSelectedLanguage,
    setOpenDropdown,
    setInputText,
    setSelectedLesson,
    setSelectedPart,
    setSelectedUnit,
    handleSend,
    handleKeyPress,
    getHistoryDetails,
    onLiveAvatarApi,
    onEndAvatarInteraction,
    downloadAvatarLog,
    videoMarkCompleted,
    lastAvatarSessionId,
    setSelectedMedium,
    isLiveAvatarLoading,
    showPopup,
    setShowPopup,
    popupMedia,
    assetData,
    setPopupMedia,
    onClickLearnVideo,
    showAskBox,
    setShowAskBox,
    audioRef,
    videoRef,
    question,
    setQuestion,
    inputRef,
    aiAudioChat,
    aiVideoChat,
    videoRes,
    audioRes,
  };
};

export default useHandler;
